import { Chatting01Icon } from '@hugeicons/react'

import { useFeedConfig, useFeedPosts } from '@/features/social-feed/model/queries'
import type {
  EventFeedConfig,
  EventPost
} from '@/features/social-feed/model/types'
import { useEventSlug } from '@/shared/hooks/useEventSlug'
import { Card } from '@/shared/ui/Card'

import { BlockEmptyState } from '../BlockEmptyState'
import { BlockSkeleton } from '../BlockSkeleton'
import { SocialFeedCarouselBlock } from './SocialFeedCarouselBlock'

import type { BlockComponentProps } from '../index'

// Default do PRD para o carrossel do modo hub — bem maior que o antigo limite
// de 2 (que existia porque `PostCard` full é alto; o carrossel não empilha).
const DEFAULT_LIMIT = 5

/**
 * Modo hub do `social_feed`: carrossel compacto (`SocialFeedCarouselBlock`)
 * no lugar da antiga pilha vertical de `PostCard` completos. Este componente
 * nunca precisa ramificar por modo internamente: quando `useHomeFeedMode()`
 * resolve `feed`, `DigitalProductHome` renderiza `FeedExperience`
 * diretamente e não itera os blocos — `SocialFeedBlock` simplesmente não é
 * montado nesse caminho.
 *
 * A visibilidade do módulo vem do menu do evento (`requires: 'forum'` no
 * registry + `useHomeBlocks`); não há toggle separado no `EventFeedConfig`.
 */
export const SocialFeedBlock = ({ block }: BlockComponentProps) => {
  const eventSlug = useEventSlug()
  const limit = block.settings?.limit ?? DEFAULT_LIMIT

  // `useFeedPosts` e `useFeedConfig` são genéricos e devolvem `unknown`; os
  // tipos do domínio vivem em `social-feed/model/types`.
  const { records, isLoading } = useFeedPosts(eventSlug, { perPage: limit })
  const { record, isLoading: isConfigLoading } = useFeedConfig(eventSlug)

  const posts = (records ?? []) as EventPost[]
  const feedConfig = record as EventFeedConfig | undefined

  // O config entra no skeleton junto com os posts: decidir antes dele chegar
  // faria o bloco aparecer e sumir a cada carga da home.
  if (isLoading || isConfigLoading) return <BlockSkeleton rows={2} />

  if (posts.length === 0)
    return (
      <BlockEmptyState
        icon={Chatting01Icon}
        title="Nenhuma publicação ainda"
        hint="As publicações do feed do evento aparecem aqui."
      />
    )

  return (
    <div className="flex flex-col gap-3">
      <Card className="card-gamification">
        <div className="flex items-center px-4 py-3">
          <h2 className="m-0 flex items-center gap-2 text-caps font-bold uppercase tracking-wide text-gray-700">
            <Chatting01Icon size={14} variant="duotone" aria-hidden />
            {block.title || 'Feed Social'}
          </h2>
        </div>
      </Card>

      <SocialFeedCarouselBlock posts={posts.slice(0, limit)} feedConfig={feedConfig} />
    </div>
  )
}
